import * as XLSX from 'xlsx';
import { ImportModelType, ImportPreviewData, FieldMapping, ValidationResult, ValidationError, ValidationWarning } from '@/types/import';
import { TEMPLATE_CONFIGS } from './excel-generator';

export interface ParsedExcelData {
  headers: string[];
  rows: Record<string, any>[];
  metadata: {
    fileName: string;
    sheetName: string;
    totalRows: number;
    columnsCount: number;
  };
}

export class ExcelParser {
  private file: File;
  private modelType: ImportModelType;

  constructor(file: File, modelType: ImportModelType) {
    this.file = file;
    this.modelType = modelType;
  }

  async parseFile(): Promise<ParsedExcelData> {
    try {
      const buffer = await this.file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: 'array' });
      
      // Get the first sheet (main data sheet)
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      
      if (!worksheet) {
        throw new Error('No worksheet found in the Excel file');
      }

      // Convert to JSON with header option
      const rawData = XLSX.utils.sheet_to_json(worksheet, { 
        header: 1,
        raw: false,
        defval: null
      }) as any[][];

      if (rawData.length === 0) {
        throw new Error('No data found in the Excel file');
      }

      // Extract headers from first row
      const headers = rawData[0] as string[];
      
      if (!headers || headers.length === 0) {
        throw new Error('No headers found in the Excel file');
      }

      // Convert data rows to objects
      const rows: Record<string, any>[] = [];
      
      for (let i = 1; i < rawData.length; i++) {
        const row = rawData[i];
        if (!row || row.every(cell => cell === null || cell === undefined || cell === '')) {
          continue; // Skip empty rows
        }

        const rowObject: Record<string, any> = {};
        headers.forEach((header, index) => {
          if (header && header.trim()) {
            rowObject[header.trim()] = this.sanitizeValue(row[index]);
          }
        });

        // Only add rows that have at least one non-empty value
        if (Object.values(rowObject).some(value => value !== null && value !== '')) {
          rows.push(rowObject);
        }
      }

      return {
        headers: headers.filter(h => h && h.trim()).map(h => h.trim()),
        rows,
        metadata: {
          fileName: this.file.name,
          sheetName,
          totalRows: rows.length,
          columnsCount: headers.length,
        },
      };
    } catch (error) {
      console.error('Error parsing Excel file:', error);
      throw new Error(`Failed to parse Excel file: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  private sanitizeValue(value: any): any {
    if (value === null || value === undefined) {
      return null;
    }
    
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed === '') return null;
      
      // Handle boolean strings
      if (trimmed.toLowerCase() === 'true') return true;
      if (trimmed.toLowerCase() === 'false') return false;
      
      // Handle number strings
      if (!isNaN(Number(trimmed)) && trimmed !== '') {
        return Number(trimmed);
      }
      
      return trimmed;
    }
    
    return value;
  }

  async validateAndPreview(): Promise<ImportPreviewData> {
    const parsedData = await this.parseFile();
    const mapping = this.generateFieldMapping(parsedData.headers);
    const validation = this.validateData(parsedData, mapping);

    return {
      headers: parsedData.headers,
      rows: parsedData.rows,
      validation,
      mapping,
    };
  }

  private generateFieldMapping(headers: string[]): FieldMapping {
    const templateConfig = TEMPLATE_CONFIGS[this.modelType];
    const templateHeaders = templateConfig.sheets[0].headers;
    const mapping: FieldMapping = {};

    headers.forEach(header => {
      // Find matching template column
      const templateColumn = templateHeaders.find(
        col => col.name.toLowerCase() === header.toLowerCase()
      );

      if (templateColumn) {
        mapping[header] = {
          databaseField: templateColumn.databaseField,
          isRequired: templateColumn.isRequired,
          dataType: templateColumn.dataType,
          foreignKeyTable: templateColumn.foreignKeyInfo?.table,
          foreignKeyColumn: templateColumn.foreignKeyInfo?.column,
          validation: templateColumn.validation,
        };
      } else {
        // Unknown column
        mapping[header] = {
          databaseField: header.toLowerCase().replace(/\s+/g, '_'),
          isRequired: false,
          dataType: 'string',
        };
      }
    });

    return mapping;
  }

  private validateData(parsedData: ParsedExcelData, mapping: FieldMapping): ValidationResult {
    const errors: ValidationError[] = [];
    const warnings: ValidationWarning[] = [];
    let validRowCount = 0;

    parsedData.rows.forEach((row, rowIndex) => {
      const rowNumber = rowIndex + 2; // +2 because Excel is 1-indexed and we skip header
      let isRowValid = true;

      // Validate each field in the row
      Object.entries(mapping).forEach(([column, fieldConfig]) => {
        const value = row[column];
        const validationResult = this.validateField(
          value,
          fieldConfig,
          column,
          rowNumber
        );

        if (validationResult.errors.length > 0) {
          errors.push(...validationResult.errors);
          isRowValid = false;
        }

        if (validationResult.warnings.length > 0) {
          warnings.push(...validationResult.warnings);
        }
      });

      // Check for required fields
      Object.entries(mapping).forEach(([column, fieldConfig]) => {
        if (fieldConfig.isRequired) {
          const value = row[column];
          if (value === null || value === undefined || value === '') {
            errors.push({
              rowNumber,
              column,
              value,
              errorType: 'required',
              message: `${column} is required but is empty`,
              suggestion: `Please provide a value for ${column}`,
            });
            isRowValid = false;
          }
        }
      });

      if (isRowValid) {
        validRowCount++;
      }
    });

    // Additional model-specific validations
    this.performModelSpecificValidation(parsedData, mapping, errors, warnings);

    return {
      isValid: errors.length === 0,
      errors,
      warnings,
      rowCount: parsedData.rows.length,
      validRowCount,
    };
  }

  private validateField(
    value: any,
    fieldConfig: FieldMapping[string],
    column: string,
    rowNumber: number
  ): { errors: ValidationError[]; warnings: ValidationWarning[] } {
    const errors: ValidationError[] = [];
    const warnings: ValidationWarning[] = [];

    // Skip validation for null/empty values (handled separately for required fields)
    if (value === null || value === undefined || value === '') {
      return { errors, warnings };
    }

    // Data type validation
    switch (fieldConfig.dataType) {
      case 'number':
        if (isNaN(Number(value))) {
          errors.push({
            rowNumber,
            column,
            value,
            errorType: 'type',
            message: `${column} must be a number`,
            suggestion: `Provide a numeric value instead of "${value}"`,
          });
        }
        break;

      case 'boolean':
        if (typeof value !== 'boolean' && !['true', 'false'].includes(String(value).toLowerCase())) {
          errors.push({
            rowNumber,
            column,
            value,
            errorType: 'type',
            message: `${column} must be true or false`,
            suggestion: `Use "true" or "false" instead of "${value}"`,
          });
        }
        break;

      case 'date':
        if (!this.isValidDate(value)) {
          errors.push({
            rowNumber,
            column,
            value,
            errorType: 'format',
            message: `${column} must be a valid date`,
            suggestion: `Use a valid date format like "YYYY-MM-DD"`,
          });
        }
        break;
    }

    // Pattern validation
    if (fieldConfig.validation && typeof value === 'string') {
      const regex = new RegExp(fieldConfig.validation);
      if (!regex.test(value)) {
        errors.push({
          rowNumber,
          column,
          value,
          errorType: 'format',
          message: `${column} does not match the required format`,
          suggestion: `Please check the format requirements for ${column}`,
        });
      }
    }

    // Model-specific validations
    this.performFieldSpecificValidation(value, fieldConfig, column, rowNumber, errors, warnings);

    return { errors, warnings };
  }

  private performFieldSpecificValidation(
    value: any,
    fieldConfig: FieldMapping[string],
    column: string,
    rowNumber: number,
    errors: ValidationError[],
    warnings: ValidationWarning[]
  ): void {
    // Specific validations based on database field
    switch (fieldConfig.databaseField) {
      case 'rating':
        if (typeof value === 'number' && (value < 0 || value > 5)) {
          errors.push({
            rowNumber,
            column,
            value,
            errorType: 'custom',
            message: 'Rating must be between 0 and 5',
            suggestion: 'Provide a rating between 0 and 5',
          });
        }
        break;

      case 'position':
        if (typeof value === 'number' && value < 0) {
          warnings.push({
            rowNumber,
            column,
            value,
            warningType: 'recommendation',
            message: 'Position should be a positive number',
          });
        }
        break;

      case 'reading_time':
        if (typeof value === 'number' && value <= 0) {
          warnings.push({
            rowNumber,
            column,
            value,
            warningType: 'recommendation',
            message: 'Reading time should be a positive number in minutes',
          });
        }
        break;

      case 'cover_bg_color':
        if (typeof value === 'string' && value.startsWith('#') && value.length !== 7) {
          warnings.push({
            rowNumber,
            column,
            value,
            warningType: 'format',
            message: 'Color should be a 6-digit hex code (e.g., #FF0000)',
          });
        }
        break;

      case 'content_type':
        if (typeof value === 'string' && !['summary', 'full_book'].includes(value.toLowerCase())) {
          errors.push({
            rowNumber,
            column,
            value,
            errorType: 'custom',
            message: 'Content Type must be "summary" or "full_book"',
            suggestion: 'Use "summary" for condensed summaries or "full_book" for complete book content',
          });
        }
        break;

      case 'people_to_roles':
        if (typeof value === 'string' && value.trim()) {
          const roles = value.split(',').map(role => role.trim()).filter(role => role);
          if (roles.length === 0) {
            warnings.push({
              rowNumber,
              column,
              value,
              warningType: 'format',
              message: 'Roles field appears to be empty after parsing',
            });
          } else {
            // Note: We can't validate against actual roles here since we don't have access to the database
            // This validation will happen during import
            const validRoleNames = ['author', 'narrator', 'translator'];
            const invalidRoles = roles.filter(role => 
              !validRoleNames.includes(role.toLowerCase())
            );
            
            if (invalidRoles.length > 0) {
              warnings.push({
                rowNumber,
                column,
                value: invalidRoles.join(', '),
                warningType: 'recommendation',
                message: `Unknown role names: ${invalidRoles.join(', ')}. Available roles: Author, Narrator, Translator`,
              });
            }
          }
        }
        break;
    }
  }

  private performModelSpecificValidation(
    parsedData: ParsedExcelData,
    mapping: FieldMapping,
    errors: ValidationError[],
    warnings: ValidationWarning[]
  ): void {
    // Check for duplicate names/titles within the file
    const nameFields = ['name', 'title'];
    
    nameFields.forEach(nameField => {
      const nameColumn = Object.keys(mapping).find(
        col => mapping[col].databaseField === nameField
      );
      
      if (nameColumn) {
        const nameValues = new Map<string, number[]>();
        
        parsedData.rows.forEach((row, index) => {
          const value = row[nameColumn];
          if (value && typeof value === 'string') {
            const normalizedValue = value.toLowerCase().trim();
            if (!nameValues.has(normalizedValue)) {
              nameValues.set(normalizedValue, []);
            }
            nameValues.get(normalizedValue)!.push(index + 2);
          }
        });

        // Report duplicates
        nameValues.forEach((rowNumbers, value) => {
          if (rowNumbers.length > 1) {
            rowNumbers.forEach(rowNumber => {
              warnings.push({
                rowNumber,
                column: nameColumn,
                value,
                warningType: 'recommendation',
                message: `Duplicate ${nameField} found in rows: ${rowNumbers.join(', ')}`,
              });
            });
          }
        });
      }
    });
  }

  private isValidDate(value: any): boolean {
    if (!value) return false;
    const date = new Date(value);
    return date instanceof Date && !isNaN(date.getTime());
  }
}

export async function parseExcelFile(file: File, modelType: ImportModelType): Promise<ImportPreviewData> {
  const parser = new ExcelParser(file, modelType);
  return await parser.validateAndPreview();
} 